import Head from 'react-helmet';
import { useSelector } from 'react-redux';
import isoConfig from 'config/isoConfig';
import {
  listingUtils_getListingImgAltText,
  listingUtils_getListingMetaDescription,
} from 'app/shared/utils/listingUtils';
import type { RootReduxState } from 'app/shared/flux/reducers';
import { getPageTitle, getStructuredData, shouldNoIndex } from './listingHeadUtils';

const DOMAIN = 'https://hotpads.com';

export const HaloListingHead = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails.currentListing);

  if (!listing) {
    return null;
  }

  const { uriV2 } = listing;
  const title = getPageTitle(listing);
  const description = listingUtils_getListingMetaDescription(listing);
  const listingImgUrl = listing.previewPhoto?.url;
  const mobilePhotoUrl = listing.medPhotoUrls?.[0] || listing.previewPhoto?.url;
  const desktopPhotoUrl = listing.largePhotos?.[0]?.url || listing.previewPhoto?.url;
  const imgAltText = listingUtils_getListingImgAltText({
    photoCaption: listing.previewPhoto?.caption,
    nameOrAddress: listing.displayName,
  });
  const structuredData = getStructuredData(listing);

  const links: Array<Record<string, string>> = [
    {
      rel: 'alternate',
      href: `android-app://com.hotpads.mobile/https/hotpads.com${uriV2}`,
    },
    {
      rel: 'preload',
      as: 'image',
      href: mobilePhotoUrl,
      media: '(max-width: 767px)',
      fetchpriority: 'high',
    },
    {
      rel: 'preload',
      as: 'image',
      href: desktopPhotoUrl,
      media: '(min-width: 768px)',
      fetchpriority: 'high',
    },
  ];

  const metas: Array<Record<string, string>> = [
    { name: 'description', content: description },
    { property: 'fb:app_id', content: isoConfig.fbid },
    // Open Graph
    { property: 'og:title', content: title },
    { property: 'og:type', content: 'website' },
    { property: 'og:image', content: listingImgUrl || '' },
    { property: 'og:image:secure_url', content: listingImgUrl || '' },
    { property: 'og:description', content: description || '' },
    { property: 'og:site_name', content: 'HotPads' },
    { property: 'og:locale', content: 'en_US' },
    // Twitter Card
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@hotpads' },
    { property: 'twitter:creator', content: '@hotpads' },
    { property: 'twitter:image:alt', content: imgAltText },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description || '' },
  ];

  if (!listing.active) {
    metas.push({
      name: 'apple-itunes-app',
      content: `app-id=345957475, app-argument=${DOMAIN + uriV2}`,
    });
  }

  if (uriV2) {
    links.push({ rel: 'canonical', href: DOMAIN + uriV2 });
    metas.push({ property: 'og:url', content: DOMAIN + uriV2 });
  }

  if (shouldNoIndex(listing)) {
    metas.push({ name: 'robots', content: 'noindex' });
  }

  return (
    <Head
      title={title}
      link={links}
      meta={metas}
      script={[
        {
          type: 'application/ld+json',
          innerHTML: JSON.stringify(structuredData),
        },
      ]}
    />
  );
};

export default HaloListingHead;
